import { HTTPClientInterface, HTTPRequest } from "@shared/core/HTTPClientInterface";
import { buildURL, SystemURL } from "./URLCatalog";

export namespace PresenteeismConciliationDetailServiceModel {
    export interface Location {
        latitude: number;
        longitude: number;
    }

    export interface Building {
        id?: string;
        name?: string;
        buildingName?: string;
        title?: string;
        description?: string;
        organizationID?: string;
        internalID?: string;
        latitude?: number;
        longitude?: number;
    }

    export interface QontactPoint {
        id?: string;
        organizationID?: string;
        buildingID?: string;
        functionalUnitID?: string;
        internalID?: string;
        triggerDistance?: number;
    }

    export interface AuditInfo {
        createdByName?: string;
        createdBy?: string;
        timestamp?: number;
        action?: string;
        recordID?: string;
    }

    export interface PresenteeismRecord {
        firestoreRecordId?: string;
        organizationID?: string;
        buildingID?: string;
        functionalUnitID?: string;
        ticketID?: string;
        requesterID?: string;
        location?: Location;
        day?: string;
        type?: string;
        timestamp?: number;
        shift?: Record<string, unknown>;
        conciliationInfo?: ConciliationInfo;
        label?: string;
        eventData?: Record<string, unknown>;
    }

    export interface ConciliationInfo {
        shift?: Record<string, unknown>;
        expectedMinutes?: number;
        minutesWorked?: number;
        minutesExtraTime?: number;
        nightMinutes?: number;
        checkoutTimestamp?: number;
        checkinTimestamp?: number;
        checkoutTicketID?: string;
        checkinTicketID?: string;
        checkinRecordID?: string;
        checkoutRecordID?: string;
        isLateCheckin?: boolean;
        isEarlyCheckout?: boolean;
        isCheckoutDayOverriden?: boolean;
        isABConciliation?: boolean;
        isUserDedicatedShift?: boolean;
        abConciliationCheckinBuilding?: Building;
        abConciliationCheckoutBuilding?: Building;
        isCanceled?: boolean;
        cancellationUndoID?: string;
        canceledReason?: string;
        canceledBy?: CanceledBy;
        canceledAt?: number;
    }

    export interface ABConciliationInfo {
        checkinBuilding?: Building;
        checkoutBuilding?: Building;
    }

    export interface CanceledBy {
        id?: string;
        sub?: string;
        name?: string;
    }

    export interface ConciliationDetailDTO {
        checkinRecordID: string;
        checkoutRecordID: string;
        tableRecordKey: string;
        organizationID: string;
        requesterID: string;
        buildingID: string;
        checkinRecord: PresenteeismRecord;
        checkoutRecord: PresenteeismRecord;
        conciliationInfo: ConciliationInfo;
        checkinLocation?: Location;
        checkoutLocation?: Location;
        checkinBuilding?: Building;
        checkoutBuilding?: Building;
        checkinQontactPoint?: QontactPoint;
        checkoutQontactPoint?: QontactPoint;
        checkinAuditInfo?: AuditInfo;
        checkoutAuditInfo?: AuditInfo;
        abConciliationInfo?: ABConciliationInfo;
        isCancelable: boolean;
        isCanceled: boolean;
        cancellationUndoID?: string;
        canceledReason?: string;
        canceledBy?: CanceledBy;
        canceledAt?: number;
    }

    export interface ConciliationDetail extends ConciliationDetailDTO {}

    export interface GetParams {
        organizationID: string;
        requesterID: string;
        day: string;
        tableRecordKey: string;
        buildingID?: string;
        checkInTimestamp?: number;
        signal?: AbortSignal;
    }

    export interface PendingCheckoutDetailDTO {
        checkinRecordID: string;
        tableRecordKey: string;
        organizationID: string;
        requesterID: string;
        buildingID: string;
        checkinRecord: PresenteeismRecord;
        checkinBuilding?: Building;
        checkinQontactPoint?: QontactPoint;
        hasShift: boolean;
    }

    export interface PendingCheckoutDetail extends PendingCheckoutDetailDTO {}

    export interface GetPendingCheckoutParams {
        organizationID: string;
        requesterID: string;
        day: string;
        tableRecordKey: string;
        buildingID?: string;
        checkInTimestamp?: number;
        signal?: AbortSignal;
    }

    export interface CancelationParams {
        organizationID: string;
        checkinRecordID: string;
        reason: string;
        signal?: AbortSignal;
    }

    export interface CancelationUndoParams {
        organizationID: string;
        checkinRecordID: string;
        signal?: AbortSignal;
    }

    export class Parser {
        static parse(item: ConciliationDetailDTO): ConciliationDetail {
            return {
                checkinRecordID: item.checkinRecordID,
                checkoutRecordID: item.checkoutRecordID,
                tableRecordKey: item.tableRecordKey,
                organizationID: item.organizationID,
                requesterID: item.requesterID,
                buildingID: item.buildingID,
                checkinRecord: item.checkinRecord ? { ...item.checkinRecord } : undefined,
                checkoutRecord: item.checkoutRecord ? { ...item.checkoutRecord } : undefined,
                conciliationInfo: item.conciliationInfo ? { ...item.conciliationInfo } : undefined,
                checkinLocation: item.checkinLocation ? { ...item.checkinLocation } : undefined,
                checkoutLocation: item.checkoutLocation ? { ...item.checkoutLocation } : undefined,
                checkinBuilding: item.checkinBuilding ? { ...item.checkinBuilding } : undefined,
                checkoutBuilding: item.checkoutBuilding ? { ...item.checkoutBuilding } : undefined,
                checkinQontactPoint: item.checkinQontactPoint ? { ...item.checkinQontactPoint } : undefined,
                checkoutQontactPoint: item.checkoutQontactPoint ? { ...item.checkoutQontactPoint } : undefined,
                checkinAuditInfo: item.checkinAuditInfo ? { ...item.checkinAuditInfo } : undefined,
                checkoutAuditInfo: item.checkoutAuditInfo ? { ...item.checkoutAuditInfo } : undefined,
                abConciliationInfo: item.abConciliationInfo ? {
                    checkinBuilding: item.abConciliationInfo.checkinBuilding
                        ? { ...item.abConciliationInfo.checkinBuilding }
                        : undefined,
                    checkoutBuilding: item.abConciliationInfo.checkoutBuilding
                        ? { ...item.abConciliationInfo.checkoutBuilding }
                        : undefined,
                } : undefined,
                isCancelable: item.isCancelable,
                isCanceled: item.isCanceled,
                cancellationUndoID: item.cancellationUndoID,
                canceledReason: item.canceledReason,
                canceledBy: item.canceledBy ? { ...item.canceledBy } : undefined,
                canceledAt: item.canceledAt,
            };
        }

        static parsePendingCheckoutDetail(item: PendingCheckoutDetailDTO): PendingCheckoutDetail {
            return {
                checkinRecordID: item.checkinRecordID,
                tableRecordKey: item.tableRecordKey,
                organizationID: item.organizationID,
                requesterID: item.requesterID,
                buildingID: item.buildingID,
                checkinRecord: item.checkinRecord ? { ...item.checkinRecord } : undefined,
                checkinBuilding: item.checkinBuilding ? { ...item.checkinBuilding } : undefined,
                checkinQontactPoint: item.checkinQontactPoint ? { ...item.checkinQontactPoint } : undefined,
                hasShift: item.hasShift,
            };
        }
    }
}

export class PresenteeismConciliationDetailService {
    private httpClient: HTTPClientInterface;

    constructor(httpClient: HTTPClientInterface) {
        this.httpClient = httpClient;
    }

    async getDetail(
        params: PresenteeismConciliationDetailServiceModel.GetParams
    ): Promise<PresenteeismConciliationDetailServiceModel.ConciliationDetail> {
        const searchParams = new URLSearchParams({
            organizationID: params.organizationID,
            requesterID: params.requesterID,
            day: params.day,
            tableRecordKey: params.tableRecordKey,
        });

        if (params.buildingID) {
            searchParams.set("buildingID", params.buildingID);
        }

        if (params.checkInTimestamp != null) {
            searchParams.set("checkInTimestamp", String(params.checkInTimestamp));
        }

        const request = new HTTPRequest();
        request.url = `${buildURL(SystemURL.Presenteeism.getConciliationDetail, {})}?${searchParams.toString()}`;
        request.method = "GET";
        request.signal = params.signal;

        const response = await this.httpClient.execute(request);
        return PresenteeismConciliationDetailServiceModel.Parser.parse(response.result);
    }

    async getPendingCheckoutDetail(
        params: PresenteeismConciliationDetailServiceModel.GetPendingCheckoutParams
    ): Promise<PresenteeismConciliationDetailServiceModel.PendingCheckoutDetail> {
        const searchParams = new URLSearchParams({
            organizationID: params.organizationID,
            requesterID: params.requesterID,
            day: params.day,
            tableRecordKey: params.tableRecordKey,
        });

        if (params.buildingID) {
            searchParams.set("buildingID", params.buildingID);
        }

        if (params.checkInTimestamp != null) {
            searchParams.set("checkInTimestamp", String(params.checkInTimestamp));
        }

        const request = new HTTPRequest();
        request.url = `${buildURL(SystemURL.Presenteeism.getPendingCheckoutDetail, {})}?${searchParams.toString()}`;
        request.method = "GET";
        request.signal = params.signal;

        const response = await this.httpClient.execute(request);
        return PresenteeismConciliationDetailServiceModel.Parser.parsePendingCheckoutDetail(response.result);
    }

    async cancelConciliation(
        params: PresenteeismConciliationDetailServiceModel.CancelationParams
    ): Promise<PresenteeismConciliationDetailServiceModel.ConciliationDetail> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Presenteeism.postConciliationCancelation, {});
        request.method = "POST";
        request.body = {
            organizationID: params.organizationID,
            checkinRecordID: params.checkinRecordID,
            reason: params.reason,
        };
        request.signal = params.signal;

        const response = await this.httpClient.execute(request);
        return PresenteeismConciliationDetailServiceModel.Parser.parse(response.result);
    }

    async undoCancelation(
        params: PresenteeismConciliationDetailServiceModel.CancelationUndoParams
    ): Promise<PresenteeismConciliationDetailServiceModel.ConciliationDetail> {
        const request = new HTTPRequest();
        request.url = buildURL(SystemURL.Presenteeism.postConciliationCancelationUndo, {});
        request.method = "POST";
        request.body = {
            organizationID: params.organizationID,
            checkinRecordID: params.checkinRecordID,
        };
        request.signal = params.signal;

        const response = await this.httpClient.execute(request);
        return PresenteeismConciliationDetailServiceModel.Parser.parse(response.result);
    }
}
